import { AfterViewInit, Directive, ElementRef, Input } from '@angular/core';
import { merge } from 'rxjs';

import { LazyLoaderService } from '@finder/shared/services/lazy-loader/lazy-loader-service';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';

declare const gsap: any;
declare const ScrollTrigger: any;

@Directive({
    selector: '[finderParallax]',
    standalone: true,
})
export class ParallaxDirective implements AfterViewInit {
    private config: any;

    @Input()
    set finderParallax(config: any) {
        this.config = config;
    }

    constructor(
        private el: ElementRef,
        private windowRef: WindowRef,
        private lazyLoader: LazyLoaderService,
    ) {

    }

    ngAfterViewInit() {
        if (!this.config) {
            return;
        }

        this.initParallax();
    }

    // TODO - proper test coverage
    /* istanbul ignore next */
    private initParallax() {
        // Load scripts
        merge(
            this.lazyLoader.lazyLoadScriptResource('gsap.js'),
            this.lazyLoader.lazyLoadScriptResource('scroll-trigger.js'),
        ).subscribe(() => {
            if (!gsap || !ScrollTrigger) {
                return;
            }

            // Register plugin
            gsap.registerPlugin(ScrollTrigger);

            if (Array.isArray(this.config)) {
                this.config.forEach(config => {
                    this.setParallax(config);
                });
            } else {
                this.setParallax(this.config);
            }
        });
    }

    // TODO - proper test coverage
    /* istanbul ignore next */
    private setParallax(config) {
        // Set class
        if (this.config.cssClass) {
            this.el.nativeElement.classList.add(this.config.cssClass);
        }

        // Set base values
        const cfg: any = {
            scrollTrigger: {
                start: 0,
                end: 'max',
                invalidateOnRefresh: true,
                scrub: 0,
            },
        };

        const targetEl = this.windowRef.nativeWindow.document.querySelectorAll(`.${config.targetClass}`);
        if (targetEl) {
            const tl = gsap.timeline(cfg);

            // We can have multiple DOM elements, and the result is a `NodeList`
            // The util converts it to am actual array
            gsap.utils.toArray(targetEl).forEach((layer: any) => {
                // Depth can be defined by a data-depth attr
                const depth = layer.dataset.depth;
                if (!depth) {
                    return;
                }

                // Modify the y axis based on the depth for much parallax magic!
                tl.to(layer, {
                    y: () => -(layer.offsetHeight * depth),
                    ease: 'none'
                }, 0);
            });
        }

        // Determine if offset is percent or number
        if (config.offset && config.offset.endsWith('%')) {
            // Chop off the '%' symbol and make sure value is number
            cfg.yPercent = Number(config.offset.slice(0, -1));
        } else if (config.speed) {
            cfg.y = 1 - parseFloat(config.speed) * ScrollTrigger.maxScroll(
                this.windowRef.nativeWindow
            );
        } else {
            if (config.offset) {
                cfg.y = Number(config.offset);
            } else {
                // We don't have a y value so don't bother...
                return;
            }
        }
        cfg.ease = 'none';

        // Setup parallax effect
        gsap.to(`.${config.targetClass}`, cfg);
    }
}
